// skipcq: JS-W1028, JS-0128
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import useReviewPhotoVariantUrl from '../../hooks/useReviewPhotoVariantUrl';

function resolveInitialIndex(readyPhotoIds, initialPhotoId) {
  if (!Array.isArray(readyPhotoIds) || readyPhotoIds.length === 0) {
    return 0;
  }
  const startIndex = readyPhotoIds.indexOf(initialPhotoId);
  return startIndex >= 0 ? startIndex : 0;
}

function LightboxDisplayImage({
  reviewType,
  reviewId,
  photoId,
  active,
}) {
  const { url, loading, error } = useReviewPhotoVariantUrl({
    reviewType,
    reviewId,
    photoId,
    variantName: 'display',
    enabled: active && Boolean(photoId),
  });

  if (!active) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} aria-label="Loading photo" />
      </Box>
    );
  }

  if (error || !url) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Photo unavailable
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt="Review photo"
      sx={{
        maxWidth: '100%',
        maxHeight: '80vh',
        objectFit: 'contain',
        display: 'block',
        mx: 'auto',
      }}
    />
  );
}

LightboxDisplayImage.propTypes = {
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  reviewId: PropTypes.string.isRequired,
  photoId: PropTypes.string,
  active: PropTypes.bool.isRequired,
};

/**
 * Full-frame viewer for ready review photos, preserving photoIds order.
 * Remount via parent `key` when opening a different photo to reset index.
 */
function ReviewPhotoLightbox({
  open,
  onClose,
  reviewType,
  reviewId,
  readyPhotoIds,
  initialPhotoId,
}) {
  const [activeIndex, setActiveIndex] = useState(
    () => resolveInitialIndex(readyPhotoIds, initialPhotoId),
  );

  const handlePrevious = useCallback(() => {
    setActiveIndex((currentIndex) => (
      currentIndex <= 0 ? readyPhotoIds.length - 1 : currentIndex - 1
    ));
  }, [readyPhotoIds.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((currentIndex) => (
      currentIndex >= readyPhotoIds.length - 1 ? 0 : currentIndex + 1
    ));
  }, [readyPhotoIds.length]);

  const activePhotoId = readyPhotoIds[activeIndex] ?? null;
  const showNavigation = readyPhotoIds.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-label="Review photo viewer"
    >
      <Box sx={{ position: 'relative', p: 2, bgcolor: 'background.paper' }}>
        <IconButton
          aria-label="Close photo viewer"
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {showNavigation ? (
          <IconButton
            aria-label="Previous photo"
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        ) : null}
        {showNavigation ? (
          <IconButton
            aria-label="Next photo"
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ) : null}
        <LightboxDisplayImage
          reviewType={reviewType}
          reviewId={reviewId}
          photoId={activePhotoId}
          active={open}
        />
        {showNavigation ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
          >
            {activeIndex + 1} of {readyPhotoIds.length}
          </Typography>
        ) : null}
      </Box>
    </Dialog>
  );
}

ReviewPhotoLightbox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  reviewId: PropTypes.string.isRequired,
  readyPhotoIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialPhotoId: PropTypes.string,
};

export default ReviewPhotoLightbox;
